import {
    buildReducer,
    HubPersona,
    PrivateDistributionList,
    PublicDistributionList,
    Person,
    isSupportedContact,
    PersonType,
    DistributionList,
} from '../../../shared';
import {
    loadPersonasCompletedAction,
    loadPersonasInSparseFolderCompletedAction,
    getPersonaCompletedAction,
    distributionListLoadCompletedAction,
    privateDistributionListNotesLoadCompletedAction,
    unlinkContactCompletedAction,
    linkPersonasCompletedAction,
    deletePersonasCompletedAction,
    deletePublicFolderPersonaCompletedAction,
    moveContactsCompletedAction,
    contactEditorNewContactSaveClickedAction,
    contactEditorExistingContactSaveClickedAction,
} from '../../../hub';

import {
    privateDLUpdateCompletedAction,
    privateDLCreateCompletedAction,
} from '../../../privatedlcreateedit';
import {
    getSearchResultsCompletedAction,
    getRecipientCacheSuggestionsCompletedAction,
    getFederatedSearchResultsCompletedAction,
    getFederatedBackfillCompletedAction,
} from '../../../searchbox';
import { lpcPersonaCreatedAction, lpcPersonaUpdatedAction } from '../../actions/lpcActions';
import type { IdsToObjectIdsMap } from '../../../hub/actions/data/moveContactsDataActions';

/**
 * This reducer is responsible for the dictionary of Personas across the app.
 * The keys in the dictionary are the personaIds of the persona
 */
export default buildReducer<{ [hubPersonaId: string]: HubPersona }>()
    .handleDataAction(getRecipientCacheSuggestionsCompletedAction, (state, action) => {
        return updateHubPersonaMap(
            state,
            action.payload.suggestions,
            false,
            false,
            false,
            true,
            true
        );
    })
    .handleDataAction(getFederatedSearchResultsCompletedAction, (state, action) => {
        return updateHubPersonaMap(
            state,
            action.payload.hubPersonas,
            false,
            false,
            false,
            true,
            true
        );
    })
    .handleDataAction(loadPersonasCompletedAction, (state, action) => {
        return updateHubPersonaMap(state, action.payload.personas);
    })
    .handleDataAction(loadPersonasInSparseFolderCompletedAction, (state, action) => {
        return updateHubPersonaMap(state, action.payload.personas);
    })
    .handle(lpcPersonaCreatedAction, (state, action) => {
        // The created persona can be auto-linked to existing persona by backend,
        // so linkedContacts must be updated
        return updateHubPersonaMap(
            state,
            [action.payload.persona],
            true /* updateLinkedContactsInfo */
        );
    })
    .handle(lpcPersonaUpdatedAction, (state, action) => {
        // The updated persona can be auto-linked to existing persona by backend,
        // so linkedContacts must be updated
        return updateHubPersonaMap(
            state,
            [action.payload.persona],
            true /* updateLinkedContactsInfo */
        );
    })
    .handle(contactEditorNewContactSaveClickedAction, (state, { payload }) => {
        // Only call if contact editor is on CAPIv3 i.e. hubContact exists
        return payload.hubContact ? updateHubPersonaMap(state, [payload.hubContact], true) : state;
    })
    .handle(contactEditorExistingContactSaveClickedAction, (state, { payload }) => {
        // Only call if contact editor is on CAPIv3 i.e. hubContact exists
        return payload.hubContact ? updateHubPersonaMap(state, [payload.hubContact], true) : state;
    })
    .handleDataAction(
        getPersonaCompletedAction,
        (state, action) => {
            return updateHubPersonaMap(state, [action.payload.persona], true);
        },
        (state, action) => {
            // GetPersona failed. In this case we treat the persona as a non-contact.
            const newState = {};
            Object.keys(state).forEach(hubPersonaId => {
                const hubPersona = state[hubPersonaId];
                if (
                    hubPersona.type === PersonType.Person &&
                    hubPersona.personaId === action.payload.personaId
                ) {
                    newState[hubPersonaId] = {
                        ...hubPersona,
                        isReadOnly: true,
                        linkedContacts: undefined,
                    };
                } else {
                    newState[hubPersonaId] = hubPersona;
                }
            });

            return newState;
        }
    )
    .handleDataAction(unlinkContactCompletedAction, (state, action) => {
        return updateHubPersonaMap(state, [action.payload.updatedPersona], true);
    })
    .handleDataAction(linkPersonasCompletedAction, (state, action) => {
        const oldPersonas = action.payload.oldHubPersonaIds.filter(
            id => id !== action.payload.linkedPersona.hubPersonaId
        );
        const updatedMap = updateHubPersonaMap(state, [action.payload.linkedPersona], true);
        oldPersonas.forEach(id => delete updatedMap[id]);
        return updatedMap;
    })
    .handleDataAction(distributionListLoadCompletedAction, (state, action) => {
        let distributionList = action.payload.distributionList;
        const existingDistributionList = state[distributionList.hubPersonaId] as DistributionList;
        if (existingDistributionList) {
            const owners =
                distributionList.type === PersonType.PublicDistributionList &&
                distributionList.owners;
            distributionList = {
                ...existingDistributionList,
                hubPersonaId: distributionList.hubPersonaId,
                personaId: distributionList.personaId,
                members: distributionList.members,
            };

            if (owners) {
                distributionList = { ...distributionList, owners } as PublicDistributionList;
            }
        }

        return updateHubPersonaMap(state, [distributionList], false, true);
    })
    .handleDataAction(privateDistributionListNotesLoadCompletedAction, (state, action) => {
        return updateStateWithNotes(state, action.payload.hubPersonaId, action.payload.notes);
    })
    .handleDataAction(privateDLCreateCompletedAction, (state, action) => {
        return updateHubPersonaMap(state, [action.payload.privateDL], false, true, true);
    })
    .handleDataAction(privateDLUpdateCompletedAction, (state, action) => {
        const existingPrivateDistributionList = state[
            action.payload.hubPersonaId
        ] as PrivateDistributionList;

        if (existingPrivateDistributionList) {
            let updatedPrivateDistributionList = { ...existingPrivateDistributionList };

            if (action.payload.newName !== undefined) {
                updatedPrivateDistributionList = {
                    ...updatedPrivateDistributionList,
                    info: {
                        listOriginalDisplayName: action.payload.newName,
                        displayNameFirstLast: action.payload.newName,
                        displayNameLastFirst: action.payload.newName,
                    },
                };
            }

            if (action.payload.newNotes !== undefined) {
                updatedPrivateDistributionList.notes = action.payload.newNotes;
            }

            if (action.payload.newMembers !== undefined) {
                updatedPrivateDistributionList.members = action.payload.newMembers;
            }

            return updateHubPersonaMap(state, [updatedPrivateDistributionList], false, true, true);
        }

        return state;
    })
    .handleDataAction(getSearchResultsCompletedAction, (state, action) => {
        return updatePersonaMapWithNewPersonas(state, action.payload.searchResult);
    })
    .handleDataAction(getFederatedBackfillCompletedAction, (state, action) => {
        return updatePersonaMapWithNewPersonas(state, action.payload.results);
    })
    .handleDataAction(deletePersonasCompletedAction, (state, action) => {
        const deletedHubPersonaIds = action.payload.deletedHubPersonaIds;

        return deletedHubPersonaIds.reduce<{ [hubPersonaId: string]: HubPersona }>(
            (updatedPersonasMap, hubPersonaId): { [hubPersonaId: string]: HubPersona } => {
                delete updatedPersonasMap[hubPersonaId];
                return updatedPersonasMap;
            },
            { ...state }
        );
    })
    .handleDataAction(deletePublicFolderPersonaCompletedAction, (state, action) => {
        const updatedState = { ...state };
        delete updatedState[action.payload.deletedHubPersonaId];

        return updatedState;
    })
    .handleDataAction(moveContactsCompletedAction, (state, action) => {
        const idsToNewObjectIdsMap = action.payload.contactIdsToNewContactIds;
        const updatedPersonasWithItemIds = action.payload.personas.map(persona => {
            // Move Contacts only supports PDLs and Personas at this moment
            switch (persona.type) {
                case PersonType.PrivateDistributionList:
                    return { ...persona, itemId: idsToNewObjectIdsMap[persona.itemId].Id };
                case PersonType.Person:
                    return updatePersonaWithNewContactsAfterMove(
                        persona,
                        idsToNewObjectIdsMap,
                        action.payload.toFolderId
                    );
                default:
                    return persona;
            }
        });
        return updateHubPersonaMap(state, updatedPersonasWithItemIds, true, false, false);
    })
    .done({});

function updatePersonaMapWithNewPersonas(
    originalMap: { [hubPersonaId: string]: HubPersona },
    hubPersonas: HubPersona[]
): { [hubPersonaId: string]: HubPersona } {
    return updateHubPersonaMap(
        originalMap,
        hubPersonas.filter(hubPersona => !originalMap[hubPersona.hubPersonaId]),
        false,
        false
    );
}

function updateHubPersonaMap(
    originalMap: { [hubPersonaId: string]: HubPersona },
    personas: HubPersona[],
    updateLinkedContactsInfo?: boolean,
    updatePdlMembers?: boolean,
    updatePdlNotes?: boolean,
    updateRelevance?: boolean,
    doNotUpdateExisting?: boolean
): { [personaId: string]: HubPersona } {
    const updatedPersonasMap = { ...originalMap };

    for (let persona of personas || []) {
        const hubPersonaId = persona.hubPersonaId;

        const existingPersona = originalMap[hubPersonaId];

        // In a scenario where an Explicit Contact is linked to a Directory contact of type Generic (Room, Booking, ...),
        // the returned PersonaTypeString in legacy FindPeople is "Person", but GetPersona returns the type from
        // the Directory (Room, Booking, ...). We need to ignore the persona type from GetPersona if we have already
        // established that the persona is of type Person, or else the persona type will change when the user clicks
        // on a contact in the hubList, and that cause various issues in the UI.
        if (
            persona.type === PersonType.Generic &&
            !!existingPersona &&
            existingPersona.type === PersonType.Person
        ) {
            const updatedPersona = {
                ...existingPersona,
                linkedContacts: persona.linkedContacts,
            };

            updatedPersonasMap[hubPersonaId] = updatedPersona;
            break;
        }

        // linkedContact must only be updated after an API call that returns the DisplayNamesArray property, as it's required
        // by getDisplayNameForSource to correctly extract the display name of each linked contact. The APIs returning
        // DisplayNamesArray are GetPersona, LinkPersonas, and UnlinkPersona. As the FolderId within linkedContacts is used
        // to check if a contact is in a Public Folder, and we need to perform that lookup after FindPeople has run, but before
        // GetPersona, we also update linkedContacts with data from FindPeople IF linkedContacts is empty (first update)
        const shouldKeepLinkedContacts =
            persona.type === PersonType.Person && !updateLinkedContactsInfo;

        // Some Pdl info (members, notes) can be retrieved only after performing a getGroupInfo call.
        // We do not want to remove that information in cases in which the additional details are not returned
        const shouldKeepPdlData =
            persona.type === PersonType.PrivateDistributionList &&
            !(updatePdlMembers && updatePdlNotes);

        const shouldKeepSomeData =
            !!existingPersona && (shouldKeepLinkedContacts || shouldKeepPdlData);

        // See comment about hiding Skype contact sources below
        const shouldFilterSomeData =
            persona.type === PersonType.Person &&
            !!persona.linkedContacts &&
            persona.linkedContacts.length > 0;

        // Cases where we have to do modification
        if (shouldKeepSomeData || shouldFilterSomeData) {
            if (persona.type === PersonType.Person) {
                const linkedContacts =
                    shouldKeepLinkedContacts &&
                    existingPersona &&
                    !!(existingPersona as Person).linkedContacts
                        ? (existingPersona as Person).linkedContacts
                        : persona.linkedContacts;

                // for public folder findPeople returns the rights of CRUD operation on each contact but
                // but getPersona does not return this, so we only update the value if the API returns it
                const canEditContact =
                    persona.canEditContact !== undefined
                        ? persona.canEditContact
                        : (existingPersona as Person)?.canEditContact;
                const canDeleteContact =
                    persona.canEditContact !== undefined
                        ? persona.canDeleteContact
                        : (existingPersona as Person)?.canDeleteContact;
                persona = {
                    ...persona,
                    // At the current moment, we hide Skype folder and contacts. It is therefore needed to
                    // also hide the Skype contact sources from the linked contacts list.
                    linkedContacts: linkedContacts && linkedContacts.filter(isSupportedContact),

                    relevancyScore:
                        updateRelevance || !existingPersona
                            ? persona.relevancyScore
                            : existingPersona.relevancyScore,

                    canEditContact,

                    canDeleteContact,
                };
            } else if (persona.type === PersonType.PublicDistributionList) {
                const shouldKeepMembers = !updatePdlMembers;
                persona = {
                    ...persona,
                    owners: shouldKeepMembers
                        ? (existingPersona as PublicDistributionList).owners
                        : persona.owners,
                    members: shouldKeepMembers
                        ? (existingPersona as PublicDistributionList).members
                        : persona.members,
                };
            } else if (persona.type === PersonType.PrivateDistributionList) {
                const shouldKeepMembers = !updatePdlMembers;
                const shouldKeepNotes = !updatePdlNotes;
                persona = {
                    ...persona,
                    members: shouldKeepMembers
                        ? (existingPersona as PrivateDistributionList).members
                        : persona.members,
                    notes: shouldKeepNotes
                        ? (existingPersona as PrivateDistributionList).notes
                        : persona.notes,
                };
            }
        }

        // Due to the way FindPeople PQv3 returns results with "email flattening", we do not want to
        // update existing personas with data from PQv3
        if (!doNotUpdateExisting || !existingPersona) {
            updatedPersonasMap[hubPersonaId] = persona;
        }
    }

    return updatedPersonasMap;
}

function updateStateWithNotes(
    state: { [personaId: string]: HubPersona },
    personaId: string,
    notes: string
): { [personaId: string]: HubPersona } {
    const existingPrivateDistributionList = state[personaId] as PrivateDistributionList;

    if (existingPrivateDistributionList) {
        const updatedPrivateDistributionList = { ...existingPrivateDistributionList, notes: notes };
        return updateHubPersonaMap(state, [updatedPrivateDistributionList], false, false, true);
    }

    return state;
}

const updatePersonaWithNewContactsAfterMove = (
    persona: Person,
    idsToNewIdsMap: IdsToObjectIdsMap,
    toFolderId: string
): HubPersona => {
    const linkedContacts = persona.linkedContacts.map(linkedContact =>
        idsToNewIdsMap[linkedContact.sourceId]
            ? {
                  ...linkedContact,
                  sourceId: idsToNewIdsMap[linkedContact.sourceId].Id,
                  sourceChangeKey: idsToNewIdsMap[linkedContact.sourceId].ChangeKey,
                  sourceFolderId: toFolderId,
                  // We might as well update sourceFolderName here, but it's not required.
                  // The only place where sourceFolderName is used is in LinkedContacts dropdown,
                  // but we always fetch info for LinkedContast from the server prior to showing it.
              }
            : linkedContact
    );
    return { ...persona, linkedContacts };
};
export type { Action } from '../../../shared';
